import React from "react";

interface LinearizationDiagramProps {
    x1: number
    x2: number
    y1?: number
    y2?: number
    unit: string
}

const WIDTH = 360;
const HEIGHT = 241;
const PADDING = 52;

const LinearizationDiagram: React.FC<LinearizationDiagramProps> = ({x1, x2, y1, y2, unit}) => {
    const safeY1 = y1 ?? 0;
    const safeY2 = y2 ?? 0;

    const minRaw = Math.min(x1, x2);
    const maxRaw = Math.max(x1, x2);
    const rawSpan = maxRaw - minRaw || 1;

    const minScaled = Math.min(safeY1, safeY2);
    const maxScaled = Math.max(safeY1, safeY2);
    const scaledSpan = maxScaled - minScaled || 1;

    // Scaled Output on the horizontal axis, Raw Input on the vertical axis.
    const plotX = (scaled: number) => PADDING + ((scaled - minScaled) / scaledSpan) * (WIDTH - 2 * PADDING);
    const plotY = (raw: number) => (HEIGHT - PADDING) - ((raw - minRaw) / rawSpan) * (HEIGHT - 2 * PADDING);

    const px1 = plotX(safeY1);
    const px2 = plotX(safeY2);
    const py1 = plotY(x1);
    const py2 = plotY(x2);

    // Midpoint of a straight line is exactly the average of its two endpoints,
    // on both axes, regardless of the underlying k/c.
    const midRaw = (x1 + x2) / 2;
    const midScaled = (safeY1 + safeY2) / 2;
    const midScaledDisplay = Math.round(midScaled * 100) / 100;
    const pxMid = plotX(midScaled);
    const pyMid = plotY(midRaw);

    return (
        <svg width={WIDTH} height={HEIGHT} viewBox={`0 0 ${WIDTH} ${HEIGHT}`}>
            <line x1={PADDING} y1={HEIGHT - PADDING} x2={WIDTH - PADDING} y2={HEIGHT - PADDING}
                  stroke="rgba(124, 144, 177, 0.4)"/>
            <line x1={PADDING} y1={PADDING} x2={PADDING} y2={HEIGHT - PADDING}
                  stroke="rgba(124, 144, 177, 0.4)"/>

            <line x1={px1} y1={py1} x2={px2} y2={py2} stroke="#1890ff" strokeWidth={2}/>
            <circle cx={px1} cy={py1} r={3} fill="#1890ff"/>
            <circle cx={px2} cy={py2} r={3} fill="#1890ff"/>
            <circle cx={pxMid} cy={pyMid} r={2.5} fill="white" stroke="#1890ff" strokeWidth={1.5}/>

            <text x={px1} y={HEIGHT - PADDING + 14} textAnchor="middle" fontSize={10} fill="#2D3C43">
                {safeY1}
            </text>
            <text x={pxMid} y={HEIGHT - PADDING + 14} textAnchor="middle" fontSize={10} fill="#7C90B1">
                {midScaledDisplay}
            </text>
            <text x={px2} y={HEIGHT - PADDING + 14} textAnchor="middle" fontSize={10} fill="#2D3C43">
                {safeY2}
            </text>
            <text x={PADDING - 4} y={py1 + 3} textAnchor="end" fontSize={10} fill="#2D3C43">
                {x1}{unit}
            </text>
            <text x={PADDING - 4} y={pyMid + 3} textAnchor="end" fontSize={10} fill="#7C90B1">
                {midRaw}{unit}
            </text>
            <text x={PADDING - 4} y={py2 + 3} textAnchor="end" fontSize={10} fill="#2D3C43">
                {x2}{unit}
            </text>

            <text x={WIDTH / 2} y={HEIGHT - 4} textAnchor="middle" fontSize={10} fill="#2D3C43">
                Scaled Value
            </text>
            <text x={12} y={HEIGHT / 2} textAnchor="middle" fontSize={10} fill="#2D3C43"
                  transform={`rotate(-90 12 ${HEIGHT / 2})`}>
                Raw ({unit})
            </text>
        </svg>
    );
};

export default LinearizationDiagram;
